/* eslint-disable max-len */
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import { ImageItemModelShape } from '@bingads-webui-react/image-picker';
import {
  EncodingPlaceholderWithStyle,
  UnverifiedPlaceholderWithStyle,
  FailedPlaceholderWithStyle
} from '@bingads-webui-campaign-react/video-status-placeholder';
import { NameEditor } from '@bingads-webui-campaign-react/image-experience';

/* istanbul ignore next */
const loadingPlaceholderGetter = (img, i18n, uploading, progressBar) => {
  if (uploading) {
    return progressBar;
  }

  if (img.status === 'Draft') {
    return <EncodingPlaceholderWithStyle placeholderContent={i18n.getString('ImageExperience_Video_Status_Encoding')} />;
  } else if (img.status === 'Unverified') {
    return <UnverifiedPlaceholderWithStyle placeholderContent={i18n.getString('ImageExperience_Video_Status_Unverified')} />;
  } else if (img.status === 'Failed') {
    return <FailedPlaceholderWithStyle placeholderContent={i18n.getString('ImageExperience_Video_Status_Failed')} />;
  }
  return null;
};

const getUserVideoItemWrapper = ({
  i18n, onUserVideoDelete, onUserVideoEdit,
}) => {
  const UserVideoItemWrapper = (props) => {
    const { children, itemHeight, image: videoThumbnail } = props;
    const [editMode, setEditMode] = useState(false);
    const [videoText] = useState(videoThumbnail.altText);
    const [editText, setEditText] = useState('');

    const thumbnailHeight = itemHeight;
    const thumbnailWidth = Math.round((videoThumbnail.width * thumbnailHeight) / videoThumbnail.height);
    const limitedThumbnailWidth =
            thumbnailWidth / thumbnailHeight > 1.6 ? thumbnailHeight * 1.6 : thumbnailWidth;

    const wrapperStyle = {
      maxHeight: thumbnailHeight + 30,
      height: thumbnailHeight + 30,
      width: limitedThumbnailWidth,
      position: 'relative',
      overflow: 'visible',
      margin: '5px',
    };

    const onDelete = (clickEvent) => {
      const imageIdString = clickEvent.currentTarget.id;

      onUserVideoDelete(imageIdString);
    };

    const onEdited = (imageIdString, newImageText) => {
      onUserVideoEdit(imageIdString, newImageText);
    };

    const statusClass = videoThumbnail.status === 'Active' ? '' : 'video-inactive';
    return (
      <div style={wrapperStyle} className="existing-image-container">
        <div className="image-section-wrapper" style={{ overflow: 'hidden' }}>
          <div className="image-section">
            <div className={`image-content-wrap ${statusClass}`} data-imageid={videoThumbnail.idString}>
              {videoThumbnail.status === 'Active' ? children :
                loadingPlaceholderGetter(videoThumbnail, i18n)}
            </div>
            <button
              id={videoThumbnail.idString}
              data-imageid={videoThumbnail.idString}
              className="image-delete glyphicon glyphicon-remove"
              onClick={onDelete}
              aria-label={i18n.getString('ImageExperience_Remove_Image')}
            />
          </div>
        </div>
        {
            !editMode &&
            (
              <div className="image-name-section">
                <span className="image-name" title={videoText}>
                  {videoText}
                </span>
                <button
                  id={videoThumbnail.idString}
                  className="image-edit glyphicon glyphicon-pencil"
                  onClick={() => { setEditText(videoText); setEditMode(true); }}
                  aria-label={i18n.getString('ImageExperience_Edit_Image_Name')}
                />
              </div>
            )
        }
        {
            editMode &&
            (
              <FocusLock
                focusFirstItemOnMount
                isControlledFocusLock
                isFocusLock={editMode}
                onExit={() => setEditMode(false)}
              >
                <NameEditor
                  id={videoThumbnail.idString}
                  value={editText}
                  i18n={i18n}
                  onChanged={e => setEditText(e.target.value)}
                  onSave={() => {
                    onEdited(videoThumbnail.idString, editText);
                    setEditMode(false);
                  }}
                  onCancel={() => setEditMode(false)}
                />
              </FocusLock>
            )
        }
      </div>
    );
  };

  UserVideoItemWrapper.propTypes = {
    children: PropTypes.element.isRequired,
    image: ImageItemModelShape.isRequired,
    itemHeight: PropTypes.number.isRequired,
  };

  return UserVideoItemWrapper;
};

export default getUserVideoItemWrapper;
